/**
 * JobTitlesPage — "المسميات الوظيفية" management.
 * Moved from ControlPanelSettings inline section into the Dropdown Lists hub
 * (/control-panel-settings/dropdowns/job-titles).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { List, Plus, Edit3, Trash2, Check, X, Loader2, Languages } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function JobTitlesPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();

  const [jobTitles, setJobTitles] = useState([]);
  const [loadingTitles, setLoadingTitles] = useState(true);
  const [newTitle, setNewTitle] = useState({ name_ar: '', name_en: '' });
  const [editingTitle, setEditingTitle] = useState(null);
  const [savingTitle, setSavingTitle] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [translatingField, setTranslatingField] = useState(null); // 'new_ar' | 'new_en'

  const headers = { Authorization: `Bearer ${token}` };

  // Translate via the LLM-backed /api/utils/translate endpoint — fills the
  // empty side automatically; the user can still override before saving.
  const autoTranslate = async ({ text, source, target, fieldKey, applyFn }) => {
    if (!text || !text.trim()) return;
    setTranslatingField(fieldKey);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/utils/translate`,
        { text: text.trim(), source, target },
        { headers },
      );
      const translated = (res.data?.translated || '').trim();
      if (translated) applyFn(translated);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في الترجمة التلقائية' : 'Auto-translate failed');
    } finally {
      setTranslatingField(null);
    }
  };

  const fetchJobTitles = async () => {
    setLoadingTitles(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dropdowns/job-titles`, { headers });
      setJobTitles(res.data.data || []);
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في جلب المسميات الوظيفية' : 'Failed to fetch job titles');
    } finally {
      setLoadingTitles(false);
    }
  };

  useEffect(() => { fetchJobTitles(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddTitle = async () => {
    if (!newTitle.name_ar || !newTitle.name_en) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    setSavingTitle(true);
    try {
      await axios.post(`${BACKEND_URL}/api/dropdowns/job-titles`, newTitle, { headers });
      toast.success(language === 'ar' ? 'تم إضافة المسمى الوظيفي' : 'Job title added');
      setNewTitle({ name_ar: '', name_en: '' });
      fetchJobTitles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error adding job title');
    } finally {
      setSavingTitle(false);
    }
  };

  const handleUpdateTitle = async (id) => {
    if (!editingTitle.name_ar || !editingTitle.name_en) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    setSavingTitle(true);
    try {
      await axios.put(`${BACKEND_URL}/api/dropdowns/job-titles/${id}`, editingTitle, { headers });
      toast.success(language === 'ar' ? 'تم تحديث المسمى الوظيفي' : 'Job title updated');
      setEditingTitle(null);
      fetchJobTitles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error updating job title');
    } finally {
      setSavingTitle(false);
    }
  };

  const handleDeleteTitle = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${BACKEND_URL}/api/dropdowns/job-titles/${id}`, { headers });
      toast.success(language === 'ar' ? 'تم حذف المسمى الوظيفي' : 'Job title deleted');
      fetchJobTitles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting job title');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <DashboardLayout
      title={language === 'ar' ? 'المسميات الوظيفية' : 'Job Titles'}
      titleEn="Job Titles"
      subtitle={language === 'ar' ? 'إدارة قائمة المسميات الوظيفية' : 'Manage job titles dropdown'}
      subtitleEn="Manage job titles dropdown"
      screenCode="DDL-002"
      backPath="/control-panel-settings/dropdowns"
      onBack={() => navigate('/control-panel-settings/dropdowns')}
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'القوائم المنسدلة', labelEn: 'Dropdown Lists', path: '/control-panel-settings/dropdowns' },
        { labelAr: 'المسميات الوظيفية', labelEn: 'Job Titles' },
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="job-titles-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Add New Title Form */}
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
              <Plus className="h-5 w-5 text-emerald-400" />
              {language === 'ar' ? 'إضافة مسمى وظيفي جديد' : 'Add New Job Title'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <div className="space-y-2">
                <label className="h-5 flex items-center text-sm text-[#A1A1AA]">
                  {language === 'ar' ? 'الاسم بالعربية' : 'Name in Arabic'}
                </label>
                <Input
                  value={newTitle.name_ar}
                  onChange={(e) => setNewTitle({ ...newTitle, name_ar: e.target.value })}
                  placeholder={language === 'ar' ? 'مثال: مدير مشروع' : 'Example: مدير مشروع'}
                  className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                  dir="rtl"
                  data-testid="new-title-ar"
                />
                <button
                  type="button"
                  onClick={() => autoTranslate({
                    text: newTitle.name_ar,
                    source: 'ar',
                    target: 'en',
                    fieldKey: 'new_en',
                    applyFn: (t) => setNewTitle((p) => ({ ...p, name_en: t })),
                  })}
                  disabled={!newTitle.name_ar.trim() || translatingField === 'new_en'}
                  className="w-full h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-cyan-500/20 transition disabled:opacity-40"
                  data-testid="translate-ar-to-en"
                >
                  {translatingField === 'new_en'
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Languages className="h-3.5 w-3.5" />}
                  {language === 'ar' ? 'ترجمة إلى الإنجليزية' : 'Translate to English'}
                </button>
              </div>
              <div className="space-y-2">
                <label className="h-5 flex items-center text-sm text-[#A1A1AA]">
                  {language === 'ar' ? 'الاسم بالإنجليزية' : 'Name in English'}
                </label>
                <Input
                  value={newTitle.name_en}
                  onChange={(e) => setNewTitle({ ...newTitle, name_en: e.target.value })}
                  placeholder="Example: Project Manager"
                  className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                  data-testid="new-title-en"
                />
                <button
                  type="button"
                  onClick={() => autoTranslate({
                    text: newTitle.name_en,
                    source: 'en',
                    target: 'ar',
                    fieldKey: 'new_ar',
                    applyFn: (t) => setNewTitle((p) => ({ ...p, name_ar: t })),
                  })}
                  disabled={!newTitle.name_en.trim() || translatingField === 'new_ar'}
                  className="w-full h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-cyan-500/20 transition disabled:opacity-40"
                  data-testid="translate-en-to-ar"
                >
                  {translatingField === 'new_ar'
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Languages className="h-3.5 w-3.5" />}
                  {language === 'ar' ? 'ترجمة إلى العربية' : 'Translate to Arabic'}
                </button>
              </div>
            </div>
            <p className="text-xs text-[#606060] mt-2">
              {language === 'ar'
                ? '💡 اكتب الاسم بإحدى اللغتين ثم اضغط زر الترجمة — يمكن التعديل قبل الحفظ.'
                : '💡 Type the name in either language then click the translate button — editable before save.'}
            </p>
            <Button
              onClick={handleAddTitle}
              disabled={savingTitle}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-title-btn"
            >
              {savingTitle ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : <Plus className="h-4 w-4 ml-2" />}
              {language === 'ar' ? 'إضافة' : 'Add'}
            </Button>
          </div>

          {/* Job Titles List */}
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-6">
            <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
              <List className="h-5 w-5 text-[#d4af37]" />
              {language === 'ar' ? 'المسميات الوظيفية الحالية' : 'Current Job Titles'}
            </h3>

            {loadingTitles ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-[#d4af37]" />
              </div>
            ) : jobTitles.length === 0 ? (
              <p className="text-center text-[#71717A] py-8">
                {language === 'ar' ? 'لا توجد مسميات وظيفية' : 'No job titles found'}
              </p>
            ) : (
              <div className="space-y-3">
                {jobTitles.map((title) => (
                  <div
                    key={title.id || title._id}
                    className="flex items-center justify-between p-4 bg-white/[0.02] rounded-xl border border-white/5"
                    data-testid={`job-title-row-${title.id || title._id}`}
                  >
                    {editingTitle && (editingTitle.id || editingTitle._id) === (title.id || title._id) ? (
                      <div className="flex-1 flex items-center gap-3">
                        <Input
                          value={editingTitle.name_ar}
                          onChange={(e) => setEditingTitle({ ...editingTitle, name_ar: e.target.value })}
                          className="bg-white/5 border-white/10 text-white flex-1"
                          dir="rtl"
                        />
                        <Input
                          value={editingTitle.name_en}
                          onChange={(e) => setEditingTitle({ ...editingTitle, name_en: e.target.value })}
                          className="bg-white/5 border-white/10 text-white flex-1"
                        />
                        <Button
                          size="sm"
                          onClick={() => handleUpdateTitle(title.id || title._id)}
                          disabled={savingTitle}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {savingTitle ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingTitle(null)}
                          className="text-[#71717A] hover:text-white"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1">
                          <p className="text-white font-medium">{title.name_ar}</p>
                          <p className="text-[#71717A] text-sm">{title.name_en}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingTitle(title)}
                            className="text-[#71717A] hover:text-[#d4af37]"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteTitle(title.id || title._id)}
                            disabled={deletingId === (title.id || title._id)}
                            className="text-[#71717A] hover:text-red-400"
                          >
                            {deletingId === (title.id || title._id) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
